import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  MessageCircle,
  CheckCircle,
  Eye,
  Play,
  Pause,
  Square,
  Users,
  BarChart3,
  Trash2,
  UserPlus,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle,
  X,
} from 'lucide-react'
import {
  useWaCampaign,
  useWaCampaignStats,
  useWaCampaignRecipients,
  useStartWaCampaign,
  usePauseWaCampaign,
  useResumeWaCampaign,
  useCancelWaCampaign,
  useRetryWaCampaign,
  useRemoveWaCampaignRecipients,
} from '@hooks/useWaCampaigns'
import { waCampaignService } from '@services/wa-campaign.service'
import { formatNumber, formatPercentage } from '@lib/utils'
import { useUIStore } from '@stores/ui'

type TabType = 'overview' | 'recipients'

export function WaCampaignDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaignId = Number(id)
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const showToast = useUIStore((state) => state.showToast)

  // Confirmation dialog state for campaign actions
  const [actionConfirm, setActionConfirm] = useState<{
    open: boolean
    type: 'cancel' | 'retry' | null
    title: string
    message: string
  }>({ open: false, type: null, title: '', message: '' })

  const { data: campaign, isLoading, error, refetch } = useWaCampaign(campaignId)
  const startMutation = useStartWaCampaign()
  const pauseMutation = usePauseWaCampaign()
  const resumeMutation = useResumeWaCampaign()
  const cancelMutation = useCancelWaCampaign()
  const retryMutation = useRetryWaCampaign()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600">Failed to load campaign details</p>
        <button
          onClick={() => navigate('/whatsapp/campaigns')}
          className="mt-4 text-sm text-red-700 underline"
        >
          Back to campaigns
        </button>
      </div>
    )
  }

  const handleStart = async () => {
    try {
      await startMutation.mutateAsync(campaignId)
      showToast('Campaign started successfully', 'success')
      refetch()
    } catch (err: any) {
      showToast(err?.message || 'Failed to start campaign', 'error')
    }
  }

  const handlePause = async () => {
    try {
      await pauseMutation.mutateAsync(campaignId)
      showToast('Campaign paused', 'success')
      refetch()
    } catch (err: any) {
      showToast(err?.message || 'Failed to pause campaign', 'error')
    }
  }

  const handleResume = async () => {
    try {
      await resumeMutation.mutateAsync(campaignId)
      showToast('Campaign resumed', 'success')
      refetch()
    } catch (err: any) {
      showToast(err?.message || 'Failed to resume campaign', 'error')
    }
  }

  const handleCancel = () => {
    setActionConfirm({
      open: true,
      type: 'cancel',
      title: 'Cancel Campaign',
      message: 'Are you sure you want to cancel this campaign? This action cannot be undone.',
    })
  }

  const handleRetry = () => {
    setActionConfirm({
      open: true,
      type: 'retry',
      title: 'Retry Campaign',
      message: 'This will reset all recipients to pending and allow you to run the campaign again. Continue?',
    })
  }

  const handleConfirmAction = async () => {
    const { type } = actionConfirm
    setActionConfirm({ open: false, type: null, title: '', message: '' })

    if (type === 'cancel') {
      try {
        await cancelMutation.mutateAsync(campaignId)
        showToast('Campaign cancelled', 'success')
        refetch()
      } catch (err: any) {
        showToast(err?.message || 'Failed to cancel campaign', 'error')
      }
    } else if (type === 'retry') {
      try {
        await retryMutation.mutateAsync(campaignId)
        showToast('Campaign reset for retry', 'success')
        refetch()
      } catch (err: any) {
        showToast(err?.message || 'Failed to retry campaign', 'error')
      }
    }
  }

  const handleCancelAction = () => {
    setActionConfirm({ open: false, type: null, title: '', message: '' })
  }

  return (
    <div className="space-y-6">
      {/* Campaign Action Confirmation Dialog */}
      {actionConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                actionConfirm.type === 'cancel' ? 'bg-red-100' : 'bg-blue-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  actionConfirm.type === 'cancel' ? 'text-red-600' : 'text-blue-600'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{actionConfirm.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{actionConfirm.message}</p>
              </div>
              <button
                onClick={handleCancelAction}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCancelAction}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={cancelMutation.isPending || retryMutation.isPending}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                  actionConfirm.type === 'cancel'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {(cancelMutation.isPending || retryMutation.isPending) ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/whatsapp/campaigns')}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{campaign.name}</h1>
            <p className="text-sm text-gray-500">Campaign ID: {campaign.id}</p>
          </div>
        </div>

        <div className="flex gap-2">
          {campaign.status === 'DRAFT' && (
            <button
              onClick={handleStart}
              disabled={startMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Play size={18} />
              Start Campaign
            </button>
          )}

          {campaign.status === 'RUNNING' && (
            <>
              <button
                onClick={handlePause}
                disabled={pauseMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-yellow-600 px-4 py-2 text-white hover:bg-yellow-700 disabled:opacity-50"
              >
                <Pause size={18} />
                Pause
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                <Square size={18} />
                Cancel
              </button>
            </>
          )}

          {campaign.status === 'PAUSED' && (
            <>
              <button
                onClick={handleResume}
                disabled={resumeMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <Play size={18} />
                Resume
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                <Square size={18} />
                Cancel
              </button>
            </>
          )}

          {(campaign.status === 'COMPLETED' || campaign.status === 'CANCELLED') && (
            <button
              onClick={handleRetry}
              disabled={retryMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw size={18} />
              Retry Campaign
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium ${
              activeTab === 'overview'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            <BarChart3 size={18} />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('recipients')}
            className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium ${
              activeTab === 'recipients'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            <Users size={18} />
            Recipients
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab campaignId={campaignId} campaign={campaign} />}
      {activeTab === 'recipients' && <RecipientsTab campaignId={campaignId} campaign={campaign} />}
    </div>
  )
}

// Overview Tab Component
function OverviewTab({ campaignId, campaign }: { campaignId: number; campaign: any }) {
  const { data: stats, isLoading: statsLoading } = useWaCampaignStats(campaignId)

  const totalRecipients = stats?.totalRecipients ?? campaign._count?.recipients ?? 0
  const sentCount = stats?.sentCount ?? 0
  const deliveredCount = stats?.deliveredCount ?? 0
  const readCount = stats?.readCount ?? 0
  const failedCount = stats?.failedCount ?? 0

  // Backend returns rates as percentages (0-100), but formatPercentage expects decimals (0-1)
  // So we normalize: if from backend, divide by 100; if calculated locally, keep as decimal
  const deliveryRate = stats?.deliveryRate != null
    ? stats.deliveryRate / 100
    : (sentCount > 0 ? deliveredCount / sentCount : 0)
  const readRate = stats?.readRate != null
    ? stats.readRate / 100
    : (deliveredCount > 0 ? readCount / deliveredCount : 0)
  const progress = totalRecipients > 0 ? (sentCount / totalRecipients) * 100 : 0

  return (
    <div className="space-y-6">
      {/* Status & Progress */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Campaign Progress</h2>
          <StatusBadge status={campaign.status} />
        </div>

        <div className="mb-2 flex justify-between text-sm text-gray-600">
          <span>Progress</span>
          <span>
            {formatNumber(sentCount)} / {formatNumber(totalRecipients)} messages sent
          </span>
        </div>
        <div className="h-4 overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full bg-green-600 transition-all duration-300"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <p className="mt-2 text-sm text-gray-500">{progress.toFixed(1)}% complete</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={MessageCircle}
          label="Messages Sent"
          value={formatNumber(sentCount)}
          color="blue"
        />
        <StatCard
          icon={CheckCircle}
          label="Delivered"
          value={formatNumber(deliveredCount)}
          subtitle={formatPercentage(deliveryRate)}
          color="green"
        />
        <StatCard
          icon={Eye}
          label="Read"
          value={formatNumber(readCount)}
          subtitle={formatPercentage(readRate)}
          color="purple"
        />
        <StatCard
          icon={XCircle}
          label="Failed"
          value={formatNumber(failedCount)}
          color="red"
        />
      </div>

      {/* Campaign Info */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Campaign Details</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="text-sm text-gray-900">{campaign.status}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Type</dt>
              <dd className="text-sm text-gray-900">{campaign.type || 'IMMEDIATE'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Total Recipients</dt>
              <dd className="text-sm text-gray-900">{formatNumber(totalRecipients)}</dd>
            </div>
            {campaign.template && (
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Template</dt>
                <dd className="text-sm text-gray-900">{campaign.template.name}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Timeline</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="text-sm text-gray-900">
                {new Date(campaign.createdAt).toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="text-sm text-gray-900">
                {new Date(campaign.updatedAt).toLocaleString()}
              </dd>
            </div>
            {campaign.scheduledAt && (
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Scheduled For</dt>
                <dd className="text-sm text-gray-900">
                  {new Date(campaign.scheduledAt).toLocaleString()}
                </dd>
              </div>
            )}
            {campaign.startedAt && (
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Started</dt>
                <dd className="text-sm text-gray-900">
                  {new Date(campaign.startedAt).toLocaleString()}
                </dd>
              </div>
            )}
            {campaign.completedAt && (
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Completed</dt>
                <dd className="text-sm text-gray-900">
                  {new Date(campaign.completedAt).toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Performance Metrics</h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <MetricCard label="Delivery Rate" value={formatPercentage(deliveryRate)} color="green" />
          <MetricCard label="Read Rate" value={formatPercentage(readRate)} color="blue" />
          <MetricCard
            label="Failure Rate"
            value={formatPercentage(sentCount > 0 ? failedCount / sentCount : 0)}
            color="red"
          />
        </div>
      </div>
    </div>
  )
}

// Recipients Tab Component
function RecipientsTab({ campaignId, campaign }: { campaignId: number; campaign: any }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const limit = 20
  const showToast = useUIStore((state) => state.showToast)

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    contactId: number | null
    contactName: string
  }>({ open: false, contactId: null, contactName: '' })

  const { data, isLoading, error } = useWaCampaignRecipients(campaignId, { page, limit })
  const removeRecipientsMutation = useRemoveWaCampaignRecipients(campaignId)

  const handleRemoveRecipient = (contactId: number, contactName: string) => {
    if (!contactId) {
      showToast('Cannot remove: contact ID is missing', 'error')
      return
    }
    // Open confirmation dialog instead of browser confirm()
    setConfirmDialog({ open: true, contactId, contactName: contactName || 'this contact' })
  }

  const handleConfirmRemove = async () => {
    const { contactId, contactName } = confirmDialog
    if (!contactId) return

    // Close dialog first
    setConfirmDialog({ open: false, contactId: null, contactName: '' })

    try {
      await removeRecipientsMutation.mutateAsync({ recipientIds: [contactId] })
      showToast(`${contactName} removed from campaign`, 'success')
    } catch (err: any) {
      showToast(err?.response?.data?.message || err?.message || 'Failed to remove recipient', 'error')
    }
  }

  const handleCancelRemove = () => {
    setConfirmDialog({ open: false, contactId: null, contactName: '' })
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600">Failed to load campaign recipients</p>
      </div>
    )
  }

  const recipients = data?.data || []
  const total = data?.pagination?.total || 0
  const totalPages = data?.pagination?.totalPages || 1

  const canAddRecipients = campaign.status === 'DRAFT'

  return (
    <div className="space-y-4">
      {/* Header with Add Recipients button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Campaign Recipients</h3>
          <p className="text-sm text-gray-500">
            {total} recipient{total !== 1 ? 's' : ''} assigned to this campaign
          </p>
        </div>
        {canAddRecipients && (
          <button
            onClick={() => navigate(`/whatsapp/contacts?addToCampaign=${campaignId}`)}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
          >
            <UserPlus size={18} />
            Add Contacts
          </button>
        )}
      </div>

      {/* Info Banner for non-draft campaigns */}
      {!canAddRecipients && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">
            Recipients can only be added or removed when the campaign is in DRAFT status.
          </p>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Remove Recipient</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Are you sure you want to remove <span className="font-medium">{confirmDialog.contactName}</span> from this campaign?
                </p>
              </div>
              <button
                onClick={handleCancelRemove}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCancelRemove}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                disabled={removeRecipientsMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {removeRecipientsMutation.isPending ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recipients Table */}
      {recipients.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <Users size={48} className="mx-auto text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No recipients assigned</h3>
          <p className="mt-2 text-sm text-gray-500">
            Add contacts to this campaign to start sending messages
          </p>
          {canAddRecipients && (
            <button
              onClick={() => navigate(`/whatsapp/contacts?addToCampaign=${campaignId}`)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
            >
              <UserPlus size={18} />
              Add Contacts
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Sent At
                </th>
                {canAddRecipients && (
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {recipients.map((recipient: any) => (
                <tr key={recipient.contactId || recipient.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {recipient.contact?.name || 'Unknown'}
                      </p>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {recipient.contact?.phone || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(recipient.status)}`}
                    >
                      {recipient.status || 'PENDING'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {recipient.sentAt ? new Date(recipient.sentAt).toLocaleString() : '-'}
                  </td>
                  {canAddRecipients && (
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <button
                        onClick={() => handleRemoveRecipient(recipient.contactId, recipient.contact?.name)}
                        disabled={removeRecipientsMutation.isPending}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        title="Remove from campaign"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}{' '}
                recipients
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function getStatusColor(status: string): string {
  switch (status?.toUpperCase()) {
    case 'SENT':
      return 'bg-blue-100 text-blue-800'
    case 'DELIVERED':
      return 'bg-green-100 text-green-800'
    case 'READ':
      return 'bg-purple-100 text-purple-800'
    case 'FAILED':
      return 'bg-red-100 text-red-800'
    case 'PENDING':
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function StatusBadge({ status }: { status: string }) {
  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    ACTIVE: 'bg-green-100 text-green-800',
    PAUSED: 'bg-yellow-100 text-yellow-800',
    COMPLETED: 'bg-blue-100 text-blue-800',
    CANCELLED: 'bg-red-100 text-red-800',
    FAILED: 'bg-red-100 text-red-800',
  }

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${statusColors[status] || statusColors.DRAFT}`}
    >
      {status}
    </span>
  )
}

interface StatCardProps {
  icon: any
  label: string
  value: string
  subtitle?: string
  color: 'blue' | 'green' | 'purple' | 'red'
}

function StatCard({ icon: Icon, label, value, subtitle, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className={`inline-flex rounded-lg p-3 ${colorClasses[color]}`}>
        <Icon size={24} />
      </div>
      <h3 className="mt-4 text-sm font-medium text-gray-600">{label}</h3>
      <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
      {subtitle && <p className="mt-2 text-sm text-gray-500">{subtitle}</p>}
    </div>
  )
}

interface MetricCardProps {
  label: string
  value: string
  color: 'green' | 'blue' | 'red'
}

function MetricCard({ label, value, color }: MetricCardProps) {
  const colorClasses = {
    green: 'text-green-600',
    blue: 'text-blue-600',
    red: 'text-red-600',
  }

  return (
    <div className="text-center">
      <p className="text-sm font-medium text-gray-600">{label}</p>
      <p className={`mt-2 text-4xl font-bold ${colorClasses[color]}`}>{value}</p>
    </div>
  )
}
